import { axiosBaseQuery } from '@api/api';
import { createApi } from '@reduxjs/toolkit/query/react';
import { AuthResponse } from '@store/api/auth/authApi';
import { OrgRole, ProjectAccessRole } from '@app-types/access';

export type NotificationType = 'invite' | 'org_transfer' | 'project_transfer';
export type InviteType = 'organization' | 'project';

export interface NotificationSender {
  userId: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  isDeleted?: boolean;
}

export interface NotificationOrganization {
  orgId: string;
  name: string;
  slug: string;
}

export interface NotificationProject {
  projectId: string;
  name: string;
  slug: string;
}

export interface NotificationProjectAccess {
  projectId: string;
  role: ProjectAccessRole;
  projectName?: string;
  projectSlug?: string;
}

export interface OrganizationInviteNotification {
  notificationType: 'invite';
  inviteType: 'organization';
  inviteId: string;
  email: string;
  role: OrgRole;
  status: 'pending';
  createdAt: string;
  expiresAt: string;
  organization: NotificationOrganization | null;
  invitedBy: NotificationSender | null;
  projectAccesses: NotificationProjectAccess[];
}

export interface ProjectInviteNotification {
  notificationType: 'invite';
  inviteType: 'project';
  inviteId: string;
  email: string;
  role: ProjectAccessRole;
  status: 'pending';
  createdAt: string;
  expiresAt: string;
  organization: NotificationOrganization | null;
  project: NotificationProject | null;
  invitedBy: NotificationSender | null;
}

export interface OrganizationTransferNotification {
  notificationType: 'org_transfer';
  transferId: string;
  orgId: string;
  status: 'pending';
  createdAt: string;
  expiresAt: string;
  organization: NotificationOrganization | null;
  initiatedBy: NotificationSender | null;
  toUser: NotificationSender | null;
}

export interface ProjectTransferNotification {
  notificationType: 'project_transfer';
  transferId: string;
  projectId: string;
  fromOrgId: string;
  toOrgId: string;
  status: 'pending';
  createdAt: string;
  expiresAt: string;
  project: NotificationProject | null;
  fromOrganization: NotificationOrganization | null;
  toOrganization: NotificationOrganization | null;
  initiatedBy: NotificationSender | null;
  toUser: NotificationSender | null;
}

export type NotificationItem =
  | OrganizationInviteNotification
  | ProjectInviteNotification
  | OrganizationTransferNotification
  | ProjectTransferNotification;

export interface NotificationsResponse {
  items: NotificationItem[];
  unreadCount: number;
  hasUnread: boolean;
  seenAt: string | null;
}

export type NotificationActionPayload =
  | {
      notificationType: 'invite';
      inviteType: InviteType;
      inviteId: string;
    }
  | {
      notificationType: 'org_transfer';
      transferId: string;
    }
  | {
      notificationType: 'project_transfer';
      transferId: string;
    };

const normalizeNotificationsResponse = (response: unknown): NotificationsResponse => {
  const typed = response as Partial<NotificationsResponse> | null | undefined;
  return {
    items: Array.isArray(typed?.items) ? (typed?.items as NotificationItem[]) : [],
    unreadCount: Number.isFinite(typed?.unreadCount) ? Number(typed?.unreadCount) : 0,
    hasUnread: Boolean(typed?.hasUnread),
    seenAt: typeof typed?.seenAt === 'string' ? typed.seenAt : null,
  };
};

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: axiosBaseQuery(),
  tagTypes: ['Notifications'],
  endpoints: builder => ({
    getNotifications: builder.query<NotificationsResponse, void>({
      query: () => ({
        url: '/auth/notifications',
      }),
      transformResponse: response => normalizeNotificationsResponse(response),
      providesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),
    markNotificationsRead: builder.mutation<{ seenAt?: string }, void>({
      query: () => ({
        url: '/auth/notifications/read',
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),
    acceptNotification: builder.mutation<AuthResponse, NotificationActionPayload>({
      query: notification => {
        if (notification.notificationType === 'invite') {
          return {
            url: `/auth/invitations/${notification.inviteType}/${notification.inviteId}/accept`,
            method: 'POST',
          };
        }
        if (notification.notificationType === 'org_transfer') {
          return {
            url: `/auth/transfers/${notification.transferId}/accept`,
            method: 'POST',
          };
        }
        return {
          url: `/auth/project-transfers/${notification.transferId}/accept`,
          method: 'POST',
        };
      },
      invalidatesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),
    declineNotification: builder.mutation<{ status?: string }, NotificationActionPayload>({
      query: notification => {
        if (notification.notificationType === 'invite') {
          return {
            url: `/auth/invitations/${notification.inviteType}/${notification.inviteId}/decline`,
            method: 'POST',
          };
        }
        if (notification.notificationType === 'org_transfer') {
          return {
            url: `/auth/transfers/${notification.transferId}/decline`,
            method: 'POST',
          };
        }
        return {
          url: `/auth/project-transfers/${notification.transferId}/decline`,
          method: 'POST',
        };
      },
      invalidatesTags: [{ type: 'Notifications', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useMarkNotificationsReadMutation,
  useAcceptNotificationMutation,
  useDeclineNotificationMutation,
} = notificationsApi;
